import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OverallScoreCard from './components/OverallScoreCard';
import StrengthsWeaknessesSection from './components/StrengthsWeaknessesSection';
import ImprovementRoadmap from './components/ImprovementRoadmap';
import TranscriptSection from './components/TranscriptSection';
import CodeReviewSection from './components/CodeReviewSection';
import ScoreBreakdown from './components/ScoreBreakdown';
import HistoricalTrend from './components/HistoricalTrend';
import BenchmarkComparison from './components/BenchmarkComparison';
import AchievementPopup from './components/AchievementPopup';
import AchievementStamps from './components/AchievementStamps';
import InterruptionInsights from './components/InterruptionInsights';
import InterviewCertificate from './components/InterviewCertificate';
import BehaviorFlagsSection from './components/BehaviorFlagsSection';
import GradingPolicySection from './components/GradingPolicySection';
import { ReportModelBadge } from './components/ReportAIDisclosure';
import ReportLoading from '../../components/ui/ReportLoading';
import { toPng } from 'html-to-image';


import * as apiSvc from '../../utils/api';

const REPORT_PENDING_MESSAGE = 'Your detailed interview report is being prepared. This is taking longer than usual, but your interview has been saved safely. You can leave this page and come back later; the report will appear automatically once ready.';
const PENDING_POLL_MS = 30 * 1000;

const InterviewReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session') || params.get('id') || location.state?.sessionId;

  const [reportData, setReportData] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showAchievement, setShowAchievement] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [profilePicBase64, setProfilePicBase64] = useState(null);
  const [reportPending, setReportPending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(REPORT_PENDING_MESSAGE);

  const getBase64FromUrl = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Error converting image to base64:', err);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    window.scrollTo(0, 0);
    if (!sessionId) {
      setError('No interview session specified');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        while (!cancelled) {
          const rData = await apiSvc.getInterviewDetail(sessionId);

          if (!rData) throw new Error('Failed to fetch report');

          if (rData.report_status === 'results_pending' || (rData.is_challenge && rData.include_report && !rData.results_released)) {
            setReportPending(false);
            setReportData(rData);
            setLoading(false);
            return;
          }

          if (rData.__httpStatus === 202 || rData.report_status) {
            setReportPending(true);
            setPendingMessage(rData.message || REPORT_PENDING_MESSAGE);
            setLoading(false);
            await wait(PENDING_POLL_MS);
            continue;
          }

          setReportPending(false);
          setReportData(rData);

          // Bust the dashboard Coach Suggestions cache
          localStorage.removeItem('prepxai_coach_suggestions');

          // Show achievement celebration
          const achievementKey = `achievement_shown_${sessionId}`;
          if (rData.achievements && rData.achievements.length > 0 && !sessionStorage.getItem(achievementKey)) {
            setShowAchievement(true);
            sessionStorage.setItem(achievementKey, 'true');
          }

          const [bData, userData] = await Promise.all([
            apiSvc.getInterviewBenchmark(sessionId),
            apiSvc.getCurrentUser()
          ]);

          if (cancelled) return;

          if (bData) {
            setBenchmarkData(bData);
          }

          if (userData) {
            setUser(userData);
            if (userData.profile_picture_url) {
              const base64 = await getBase64FromUrl(userData.profile_picture_url);
              if (!cancelled) {
                setProfilePicBase64(base64);
              }
            }
          }

          return;
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);


  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-100 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-[10px] font-black text-slate-400 tracking-[0.2em] animate-pulse">Retrieving Analysis...</p>
        </div>
      </div>
    );
  }

  if (reportPending) {
    return <ReportLoading takingLong message={pendingMessage} />;
  }

  if (reportData?.report_status === 'results_pending' || (reportData?.is_challenge && reportData?.include_report && !reportData?.results_released)) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <NavigationHeader />
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 p-8 shadow-sm flex flex-col items-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-5">
            <Icon name="Trophy" size={32} className="text-amber-500" />
          </div>
          <span className="text-[11px] font-bold text-amber-700 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full border border-amber-200 mb-3">
            Results Pending
          </span>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Submission Received</h1>
          <p className="text-slate-600 text-xs leading-relaxed mb-6">
            Your challenge interview has been completed and securely submitted.
            Detailed feedback, AI analysis, and scores will be made available once the official results are announced.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#059669] hover:bg-emerald-700 text-white rounded-xl py-2.5 text-xs font-semibold"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (error || !reportData?.overallData) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-6">
          <Icon name="AlertCircle" size={32} className="text-rose-500" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2 uppercase">Analysis Unavailable</h1>
        <p className="text-slate-500 mb-8 max-w-md">{error || 'Unable to load your report. This might be due to a short session.'}</p>
        <Button variant="default" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const {
    overallData, strengths, weaknesses, recommendations,
    transcript, annotations, timeAllocation, codeQuality,
    communicationPattern, codeSnippets, problemReviews,
    radarData, parameters, achievements
  } = reportData;

  const nextStep = recommendations?.find(r => r.priority === 'high') || recommendations?.[0];
  const benchmarkTopPercent = benchmarkData?.available
    ? (benchmarkData.top_percent ?? Math.round(100 - benchmarkData.percentile))
    : 0;

  const handleShareAchievement = async (platform) => {
    const node = document.getElementById('capture-certificate');
    if (!node) return;

    try {
      setIsSharing(true);
      const dataUrl = await toPng(node, {
        quality: 0.95,
        backgroundColor: '#ffffff',
        useCORS: true,
        cacheBust: true
      });

      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'interview-achievement.png', { type: 'image/png' });

      // If Web Share API is available and supports files (mostly mobile)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Interview Achievement',
          text: rData.is_challenge && !rData.results_released 
            ? `I just completed my interview on prepxai! I scored ${overallData.score}% and the competitive rankings will be out soon.`
            : `Check out my interview performance on prepxai! I scored ${overallData.score}% and ranked in the top ${overallData.topPercentile}% worldwide.`,
        });
      } else {
        // Fallback: Download the image and show instructions
        const link = document.createElement('a');
        link.download = 'my-prep-x-achievement.png';
        link.href = dataUrl;
        link.click();

        // Platform specific URLs for text sharing
        const message = encodeURIComponent(`I just crushed my AI interview! 🚀 Scored ${overallData.score}% on prepxai. #InterviewPrep #Coding`);
        const reportUrl = encodeURIComponent(window.location.href);

        if (platform === 'whatsapp') {
          window.open(`https://wa.me/?text=${message}%20${reportUrl}`, '_blank');
        } else if (platform === 'linkedin') {
          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${reportUrl}`, '_blank');
        }
      }
    } catch (err) {
      console.error('Error sharing certificate:', err);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-emerald-100 selection:text-emerald-900 relative overflow-hidden">
      {/* Decorative Premium Gradients */}
      <div className="absolute top-0 left-0 w-full h-[50vh] bg-gradient-to-b from-emerald-500/[0.08] to-transparent pointer-events-none dark:hidden" />
      <div className="absolute top-0 right-0 w-full h-screen bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.05),transparent)] pointer-events-none dark:hidden" />
      <div className="absolute top-[20%] left-0 w-full h-screen bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.03),transparent)] pointer-events-none dark:hidden" />

      <NavigationHeader />

      <main className="pt-14 pb-12 px-8 sm:px-12 lg:px-24">
        <AchievementPopup
          isOpen={showAchievement}
          onClose={() => setShowAchievement(false)}
          score={overallData?.score}
          interviewType={overallData?.interviewType}
          userName={user?.full_name || 'Student'}
        />

        <div className="max-w-[1400px] mx-auto space-y-4">
          {/* Navigation & Actions */}
          <div className="flex items-center justify-end pb-4">

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl h-9 px-4 text-[10px] font-black tracking-wider border-slate-200 text-slate-600 hover:bg-emerald-50/50 hover:text-emerald-500 hover:border-emerald-200 transition-all"
                loading={isSharing}
                onClick={() => handleShareAchievement('linkedin')}
              >
                <Icon name="Share2" size={14} className="mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl h-9 px-4 text-[10px] font-black tracking-wider border-slate-200 text-slate-600 hover:bg-emerald-50/50 hover:text-emerald-500 hover:border-emerald-200 transition-all"
                onClick={() => window.open(`/certificate/${sessionId}`, '_blank')}
              >
                <Icon name="Award" size={14} className="mr-2" />
                Certificate
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl h-9 px-4 text-[10px] font-black tracking-wider border-slate-200 text-slate-600 hover:bg-emerald-50/50 hover:text-emerald-500 hover:border-emerald-200 transition-all"
                onClick={window.print}
              >
                <Icon name="Download" size={14} className="mr-2" />
                PDF
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="rounded-xl h-9 px-4 text-[10px] font-black tracking-wider bg-emerald-500 text-slate-900 shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 hover:scale-105 active:scale-95 transition-all"
                onClick={() => navigate('/interview-setup')}
              >
                Retake
              </Button>
            </div>
          </div>

          {/* Hero Section (Reverted to Left Aligned) */}
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10 py-2">
            {/* Left: Message & Context */}
            <div className="space-y-3">
              {/* <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-slate-50 rounded-full border border-slate-100">
                <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[9px] font-black text-slate-400 tracking-widest leading-none">Analysis Decoded</span>
              </div> */}
              <ReportModelBadge />

              {(() => {
                const percent = benchmarkData?.available ? benchmarkTopPercent : 50;
                let message = "Keep Growing";
                if (percent <= 5) message = "Legendary Performance";
                else if (percent <= 15) message = "Elite Performance";
                else if (percent <= 30) message = "Strong Potential";
                else if (percent <= 50) message = "Solid Foundation";

                return (
                  <div className="space-y-2">
                    <h1 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight tracking-tight">
                      {message}, {user?.full_name?.split(' ')[0] || 'Explorer'}!<br />
                      {reportData?.is_challenge && !reportData?.results_released ? (
                        <>
                          <span className="text-slate-900">Your competitive </span>
                          <span className="text-3xl md:text-4xl text-emerald-500 inline-block mt-2 font-black tracking-tighter">
                            Rank is TBD
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-slate-900">You are in the </span>
                          <span className="text-3xl md:text-4xl text-emerald-500 inline-block mt-2 font-black tracking-tighter">
                            Top {percent}%
                          </span>
                        </>
                      )}
                    </h1>
                  </div>
                );
              })()}

              <div className="flex flex-wrap items-center gap-5 pt-1">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Icon name="Briefcase" size={12} className="opacity-60" />
                  <span className="text-[9px] font-black text-slate-400 tracking-widest">
                    {overallData?.interviewType} {reportData?.company ? `@ ${reportData.company}` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Icon name="Clock" size={12} className="opacity-60" />
                  <span className="text-[9px] font-black text-slate-400 tracking-widest">
                    {overallData?.duration} MINS
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Icon name="Calendar" size={12} className="opacity-60" />
                  <span className="text-[9px] font-black text-slate-400 tracking-widest">
                    {overallData?.date}
                  </span>
                </div>
              </div>

              {/* Codeforces-style Achievement Stamps */}
              <AchievementStamps achievements={achievements} />
            </div>

            {/* Score Card */}
            <div className="shrink-0">
              <OverallScoreCard
                {...overallData}
                topPercent={benchmarkTopPercent}
                evaluationMeta={reportData?.evaluationMeta}
                integrityScore={reportData?.integrityScore}
              />
            </div>
          </div>

          {reportData?.gradingPolicy && (
            <GradingPolicySection
              gradingPolicy={reportData?.gradingPolicy}
              checkpointScores={reportData?.checkpointScores}
              parameters={parameters}
            />
          )}

          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-500">
                <Icon name="Target" size={18} />
              </div>
              <h2 className="text-base font-black text-slate-900 tracking-wider">Global Benchmarking</h2>
            </div>

            <BenchmarkComparison
              benchmarkData={reportData?.is_challenge && !reportData?.results_released ? { ...benchmarkData, available: false } : benchmarkData}
            />
          </div>

          {/* 2b. Historical Trend (shows score progression and deltas) */}
          <HistoricalTrend
            historicalTrend={reportData?.historicalTrend}
            deltaVsPrevious={reportData?.deltaVsPrevious}
            radarData={radarData}
          />

          {/* 3. Detailed Performance & Score Breakdown (Merged component) */}
          <ScoreBreakdown
            parameters={parameters}
            overallScore={overallData?.score}
            deltaVsPrevious={reportData?.deltaVsPrevious}
            interviewType={overallData?.interviewType}
          />

          {/* 3b. Interruption & Filler Word Insights */}
          <InterruptionInsights transcript={transcript} computedMetrics={reportData?.computedMetrics} />

          {/* 3c. Interview Integrity & Behavior Flags */}
          <BehaviorFlagsSection
            behaviorFlags={reportData?.behaviorFlags}
            integrityScore={reportData?.integrityScore}
            computedMetrics={reportData?.computedMetrics}
          />

          {/* 4. Strengths & Weaknesses */}
          <div className="mt-8">
            <StrengthsWeaknessesSection strengths={strengths} weaknesses={weaknesses} />
          </div>

          {/* 5. Personalized Growth Plan */}
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-500 shadow-sm border border-emerald-100">
                <Icon name="Map" size={18} />
              </div>
              <h2 className="text-sm font-black text-slate-900 tracking-wider">Personalized Plan</h2>
            </div>
            <ImprovementRoadmap recommendations={recommendations} />
          </div>

          {/* 5. Problem Review Section */}
          {problemReviews && problemReviews.length > 0 && (
            <div className="mt-8">
              <CodeReviewSection
                snippets={codeSnippets}
                codeFeedback={reportData?.codeFeedback}
                codeSuggestions={reportData?.codeSuggestions}
                codeRating={reportData?.codeRating}
                codeStrengths={reportData?.codeStrengths}
                codeWeaknesses={reportData?.codeWeaknesses}
                problemReviews={problemReviews}
              />
            </div>
          )}



          {/* Full Transcript Section */}
          <div className="mt-8">
            <TranscriptSection transcript={transcript} annotations={annotations} computedMetrics={reportData?.computedMetrics} />
          </div>

          {/* AI Suggested Next Step - Premium Highlight */}
          {nextStep && (
            <div className="mt-12 bg-slate-900 rounded-[3rem] p-8 md:p-12 text-center relative overflow-hidden group border border-emerald-500/20 shadow-2xl shadow-emerald-500/10">
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.15),transparent)] pointer-events-none" />
              <div className="relative z-10 max-w-2xl mx-auto">

                <h2 className="text-2xl md:text-3xl font-semibold !text-white mb-4 tracking-tight leading-tight">
                  Ready for Another One?
                </h2>
                <p className="!text-white text-sm md:text-base mb-10 leading-relaxed font-normal">
                  {nextStep.description}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button
                    variant="default"
                    onClick={() => navigate('/interview-setup')}
                    className="w-full sm:w-auto px-10 h-14 bg-emerald-500 text-slate-900 rounded-2xl font-medium tracking-widest text-[10px] hover:bg-emerald-400 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-3"
                  >
                    Let's Do It Again!
                    <Icon name="ArrowRight" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* End of report sections */}
        </div>

        {/* Hidden Certificate for Capture */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <InterviewCertificate
            userName={user?.full_name || 'PrepX Student'}
            role={overallData?.interviewType || 'Software Engineer'}
            score={overallData?.score || 0}
            topPercent={reportData?.is_challenge && !reportData?.results_released ? null : benchmarkTopPercent}
            id={`PX-${sessionId?.slice(-8).toUpperCase()}`}
            profilePicture={profilePicBase64 || user?.profile_picture_url}
            metrics={{
              architecture: codeQuality?.[0]?.score || 75,
              problemSolving: codeQuality?.[1]?.score || 80,
              communication: communicationPattern?.[0]?.score || 85
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default InterviewReport;
